import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CSpinner,
  CCard, CCardHeader, CCardBody,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilWarning, cilFile, cilPencil,
  cilArrowLeft, cilPen, cilTrash,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { usePendiente } from '../components/ww/pendiente'

// Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
// empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

const AtrasoViewPage: React.FC = () => {
  const { nro } = useParams<{ nro: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [atraso, setAtraso] = useState<any>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [eliminarAbierto, setEliminarAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()

  useEffect(() => {
    setCargando(true)
    setError('')
    api.get<any[]>(`/atrasos?empresa=${empresa}`)
      .then((lista) => {
        const encontrado = (lista ?? []).find((a) => String(a.atrazoNro ?? '') === String(nro ?? ''))
        if (!encontrado) setError('Atraso no encontrado')
        setAtraso(encontrado ?? null)
      })
      .catch((e: any) => setError(e.message ?? 'Atraso no encontrado'))
      .finally(() => setCargando(false))
  }, [nro, empresa])

  const v = (valor: any): string => (valor === null || valor === undefined || valor === '' ? '—' : String(valor))

  const fila = (label: string, valor: any) => (
    <CCol md={6}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-medium text-break">{valor ?? '—'}</div>
    </CCol>
  )

  const tarjeta = (icono: any, titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icono} size="sm" /> {titulo}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  const modificar = () => {
    setAviso('La modificación está pendiente de réplica (no existe endpoint de actualización en el backend)')
  }

  const confirmarEliminar = () => {
    setEliminarAbierto(false)
    setAviso('La eliminación está pendiente de réplica (no existe endpoint DELETE en el backend)')
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !atraso) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Atraso</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/atrasos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Atraso no encontrado</CAlert>
      </>
    )
  }

  return (
    <>
      <CRow className="mb-4">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Atraso {nro}</h4>
          </div>
          <div className="d-flex gap-2">
            <CButton color="secondary" variant="outline" onClick={() => navigate('/atrasos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
            <CButton color="primary" onClick={modificar}>
              <CIcon icon={cilPen} className="me-1" size="sm" />
              Modificar
            </CButton>
            <CButton color="danger" onClick={() => setEliminarAbierto(true)}>
              <CIcon icon={cilTrash} className="me-1" size="sm" />
              Eliminar
            </CButton>
          </div>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {tarjeta(cilWarning, 'Atraso', (
        <>
          {fila('Nro', atraso?.atrazoNro)}
          {fila('Fecha', v(atraso?.atrazoFecha).slice(0, 10))}
          {fila('Hora', atraso?.atrazoHora)}
          {fila('Cuotas', atraso?.atrazoCuotas)}
          {fila('Observación', atraso?.atrazoObservacion)}
          {fila('Próximo Cobro', v(atraso?.prestamoProximoCobro).slice(0, 10))}
          {fila('Creado Por', atraso?.atrazoCreadoPor)}
        </>
      ))}

      {tarjeta(cilFile, 'Préstamo', (
        <>
          {fila('Nro', atraso?.prestamoNro)}
          {fila('Cédula', atraso?.clienteCedula)}
          {fila('Nombre', atraso?.clienteNombre)}
          {fila('WhatsApp', atraso?.clienteCelular)}
          {fila('Ruta', atraso?.zonaNombre)}
          {fila('Cobrador', atraso?.cobrador)}
          {fila('Saldo', fmtMoneda(atraso?.prestamoSaldo))}
          {fila('Cuota', fmtMoneda(atraso?.prestamoCuota))}
          {fila('Mora', atraso?.prestamoDiasMora)}
          {fila('Atrasos', atraso?.prestamoAtrazos)}
          {fila('Condenados', atraso?.prestamoAtrazosCondenados)}
          {fila('Total Atrasos Condenados', fmtMoneda(atraso?.totalAtrasosCondenados))}
        </>
      ))}

      {tarjeta(cilPencil, 'Observación', (
        <>
          <CCol md={12}>
            <div className="small text-body-secondary">Observación</div>
            <div className="fw-medium text-break">{v(atraso?.atrazoObservacion)}</div>
          </CCol>
        </>
      ))}

      <CModal visible={eliminarAbierto} onClose={() => setEliminarAbierto(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-1 text-danger" />
            Eliminar atraso
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            ¿Eliminar el atraso <strong>{nro}</strong>?
          </p>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEliminarAbierto(false)}>Cancelar</CButton>
          <CButton color="danger" type="button" onClick={confirmarEliminar}>
            Eliminar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AtrasoViewPage
